import React, { useContext, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Button } from 'ncoded-component-library';
import { useLocation } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import useEffectSkipFirst from 'hooks/useEffectSkipFirst';
import bus, { StandardEventTypes } from 'modules/bus';
import ArrownarrowupIcon from 'icons/Arrownarrowup.icon';
import {
  DASH_DEFAULT_CLASSES,
  MainLayoutContext,
} from 'router/subrouters/Dash/Dash.router';
import Portal from 'components/Portal';
import useScrollLock from 'hooks/useScrollLock';
import useMatchMedia from 'hooks/useMatchMedia';
import ResolveHeaderDesktop from './components/ResolveHeaderDesktop';
import ResolveHeaderMobile from './components/ResolveHeaderMobile';
import { scrollToTop } from 'utils';
import { BrandingLogos } from 'types';
import SearchFormWithRecentSearches from './components/SearchFormWithRecentSearches';
import SearchContext from 'providers/Search/Search.context';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';

import './ResolveHeader.styles.scss';

const HIDE_SCROLL_TO_TOP_PAGES = ['messaging', 'leads', 'data-room'];

type ResolveHeaderProps = {
  className?: string;
  brandingLogos?: BrandingLogos;
  hasSearch?: boolean;
  hasMobileSearch?: boolean;
  hasMessagingLink?: boolean;
  handleOnSearch?: () => void;
};

const ResolveHeader: React.FC<ResolveHeaderProps> = (props) => {
  const {
    children,
    className,
    brandingLogos,
    hasMessagingLink = true,
    hasSearch = true,
    hasMobileSearch = false,
    handleOnSearch,
  } = props;

  const {
    params: { searchString },
  } = useQueryParams<{ searchString: string; page: string }>();

  const [windowScrolled, setWindowScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isTablet = useMatchMedia('isTablet');

  const classes = useMemo(
    () => classNames('resolve-header', className),
    [className],
  );

  const { pathname } = useLocation();

  const isOnMessagingPage = useMemo(
    () => pathname.includes('dash/messaging'),
    [pathname],
  );

  const hideScrollToTopButton = useMemo(
    () => HIDE_SCROLL_TO_TOP_PAGES.some((page) => pathname.includes(page)),
    [pathname],
  );

  const { isSearchOpen } = useContext(SearchContext);

  const [mobileSearchBarEl, mobileSearchBarRef] =
    useCallbackRef<HTMLDivElement>();

  useHeightAnimation({
    element: mobileSearchBarEl,
  });

  const { lockScroll, unlockScroll } = useScrollLock();

  const { setDashClassName, resetDashClassName } =
    useContext(MainLayoutContext);

  useEffectSkipFirst(() => {
    bus.broadcastEvent(StandardEventTypes.LISTING_SEARCH, searchString || '');
  }, [searchString]);

  useEffect(() => {
    window.onscroll = () => setWindowScrolled(!!window.scrollY);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) lockScroll();

    return () => unlockScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileMenuOpen]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    setDashClassName?.(
      classNames(DASH_DEFAULT_CLASSES, {
        'messaging-wrapper': isOnMessagingPage,
        'header-mobile-open': isMobileMenuOpen,
      }),
    );
  }, [
    isOnMessagingPage,
    isMobileMenuOpen,
    resetDashClassName,
    setDashClassName,
  ]);

  return (
    <>
      <header className={classes}>
        {isTablet ? (
          <ResolveHeaderMobile
            brandingLogos={brandingLogos}
            hasSearch={hasMobileSearch}
            hasSearchForm={hasSearch}
            hasMessagingLink={hasMessagingLink}
            isMenuOpen={isMobileMenuOpen}
            setIsOpenMenu={setIsMobileMenuOpen}
            handleOnSearch={handleOnSearch}
          >
            {children}
          </ResolveHeaderMobile>
        ) : (
          <ResolveHeaderDesktop
            brandingLogos={brandingLogos}
            hasSearch={hasSearch}
            hasMessagingLink={hasMessagingLink}
          />
        )}

        {!hideScrollToTopButton && (
          <Portal isOpen={windowScrolled}>
            <Button
              variant="icon"
              className="resolve-header__scroll-top"
              aria-label="Scroll to top."
              icon={ArrownarrowupIcon}
              onClick={scrollToTop}
            />
          </Portal>
        )}
      </header>

      {isTablet && hasSearch && (
        <div
          ref={mobileSearchBarRef}
          className={classNames('mobile-search-bar', {
            floating: isSearchOpen && windowScrolled,
          })}
        >
          {isSearchOpen && (
            <div className="mobile-search-bar__inner">
              <SearchFormWithRecentSearches />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ResolveHeader;
